import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet } from '../types/timesheet.ts';
import { Plus, FileText, Upload, Download, Filter, Search, Calendar, Trash2, Edit, FileSpreadsheet, Eye, Users, CheckSquare, Square, Package, ChevronDown, ChevronRight, MinusSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TimesheetDetailView from './TimesheetDetailView.tsx';

const TimesheetManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [filteredTimesheets, setFilteredTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState<WeeklyTimesheet | null>(null);
  const [selectedTimesheets, setSelectedTimesheets] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');

  useEffect(() => {
    fetchTimesheets();
  }, []);
  
  // Auto-expand the most recent week
  useEffect(() => {
    if (filteredTimesheets.length > 0 && expandedWeeks.size === 0) {
      const groupedTimesheets = getGroupedTimesheets();
      if (groupedTimesheets.length > 0) {
        setExpandedWeeks(new Set([groupedTimesheets[0].weekEnding]));
      }
    }
  }, [filteredTimesheets]);

  useEffect(() => {
    applyFilters();
  }, [timesheets, searchTerm, statusFilter, dateFilter]);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      const q = query(timesheetsRef, orderBy('weekEnding', 'desc'));
      const snapshot = await getDocs(q);
      
      const timesheetData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        weekEnding: doc.data().weekEnding?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as WeeklyTimesheet[];
      
      setTimesheets(timesheetData);
    } catch (err) {
      setError('Failed to fetch timesheets');
      console.error('Error fetching timesheets:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...timesheets];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(ts => 
        ts.jobName.toLowerCase().includes(searchLower) ||
        ts.jobNumber?.toLowerCase().includes(searchLower) ||
        ts.entries.some(entry => entry.employeeName.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(ts => ts.status === statusFilter);
    }

    // Date filter - match timesheets by week ending date
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      
      filtered = filtered.filter(ts => {
        const tsDate = new Date(ts.weekEnding);
        // Compare just the date parts (ignore time)
        const filterDateString = filterDate.toISOString().split('T')[0];
        const tsDateString = tsDate.toISOString().split('T')[0];
        return tsDateString === filterDateString;
      });
    }

    setFilteredTimesheets(filtered);
  };

  // Group timesheets by week ending date, then by contractor within each week
  const getGroupedTimesheets = () => {
    const weekGroups: { [key: string]: WeeklyTimesheet[] } = {};
    
    // Group by week ending date
    filteredTimesheets.forEach(timesheet => {
      // Create a local date string to avoid timezone issues
      const year = timesheet.weekEnding.getFullYear();
      const month = String(timesheet.weekEnding.getMonth() + 1).padStart(2, '0');
      const day = String(timesheet.weekEnding.getDate()).padStart(2, '0');
      const weekEndingKey = `${year}-${month}-${day}`;
      
      if (!weekGroups[weekEndingKey]) {
        weekGroups[weekEndingKey] = [];
      }
      weekGroups[weekEndingKey].push(timesheet);
    });
    
    // Sort weeks by date (most recent first) and group timesheets by contractor within each week
    const sortedGroups = Object.entries(weekGroups)
      .sort(([dateA], [dateB]) => new Date(dateB + 'T12:00:00').getTime() - new Date(dateA + 'T12:00:00').getTime())
      .map(([date, timesheets]) => {
        // Group timesheets by contractor within this week
        const contractorGroups: { [key: string]: WeeklyTimesheet[] } = {};
        
        timesheets.forEach(timesheet => {
          const contractorKey = timesheet.contractorName || timesheet.contractorId || 'No Contractor';
          if (!contractorGroups[contractorKey]) {
            contractorGroups[contractorKey] = [];
          }
          contractorGroups[contractorKey].push(timesheet);
        });
        
        // Sort contractors by total hours (most to least) and timesheets within each contractor by total hours
        const sortedContractorGroups = Object.entries(contractorGroups)
          .sort(([contractorA, timesheetsA], [contractorB, timesheetsB]) => {
            if (contractorA === 'No Contractor') return 1;
            if (contractorB === 'No Contractor') return -1;
            const totalHoursA = timesheetsA.reduce((total, ts) => total + getTotalHours(ts), 0);
            const totalHoursB = timesheetsB.reduce((total, ts) => total + getTotalHours(ts), 0);
            return totalHoursB - totalHoursA; // Most hours first
          })
          .map(([contractorName, contractorTimesheets]) => ({
            contractorName,
            timesheets: contractorTimesheets.sort((a, b) => getTotalHours(b) - getTotalHours(a))
          }));
        
        return {
          weekEnding: date,
          displayDate: (() => {
            // Parse date components to avoid timezone issues
            const [year, month, day] = date.split('-').map(num => parseInt(num));
            const localDate = new Date(year, month - 1, day); // months are 0-indexed
            return localDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            });
          })(),
          contractors: sortedContractorGroups,
          totalTimesheets: timesheets.length,
          totalHours: timesheets.reduce((total, ts) => total + getTotalHours(ts), 0)
        };
      });
    
    return sortedGroups;
  };

  const handleDeleteTimesheet = async (timesheet: WeeklyTimesheet) => {
    if (!window.confirm(`Are you sure you want to delete the timesheet for "${timesheet.jobName}" (Week ending ${timesheet.weekEnding.toLocaleDateString()})?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'weeklyTimesheets', timesheet.id));
      setSuccess('Timesheet deleted successfully');
      fetchTimesheets();
    } catch (err) {
      setError('Failed to delete timesheet');
      console.error('Error deleting timesheet:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processed_for_invoice': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTotalHours = (timesheet: WeeklyTimesheet) => {
    return timesheet.entries.reduce((total, entry) => {
      return total + Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
    }, 0);
  };

  const getEmployeeCount = (timesheet: WeeklyTimesheet) => {
    return timesheet.entries.length;
  };
  
  const toggleWeekExpansion = (weekKey: string) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(weekKey)) {
      newExpanded.delete(weekKey);
    } else {
      newExpanded.add(weekKey);
    }
    setExpandedWeeks(newExpanded);
  };

  const toggleTimesheetSelection = (timesheetId: string) => {
    const newSelection = new Set(selectedTimesheets);
    if (newSelection.has(timesheetId)) {
      newSelection.delete(timesheetId);
    } else {
      newSelection.add(timesheetId);
    }
    setSelectedTimesheets(newSelection);
  };
  
  const selectWeekTimesheets = (weekTimesheets: WeeklyTimesheet[]) => {
    const newSelection = new Set(selectedTimesheets);
    const convertibleTimesheets = weekTimesheets.filter(ts => 
      ts.status === 'completed' || ts.status === 'processed_for_invoice'
    );
    
    // Check if all convertible timesheets in this week are already selected
    const allSelected = convertibleTimesheets.every(ts => newSelection.has(ts.id));
    
    if (allSelected) {
      // Deselect all timesheets from this week
      convertibleTimesheets.forEach(ts => newSelection.delete(ts.id));
    } else {
      // Select all convertible timesheets from this week
      convertibleTimesheets.forEach(ts => newSelection.add(ts.id));
    }
    
    setSelectedTimesheets(newSelection);
  };
  
  const selectContractorTimesheets = (contractorTimesheets: WeeklyTimesheet[]) => {
    const newSelection = new Set(selectedTimesheets);
    const convertibleTimesheets = contractorTimesheets.filter(ts => 
      ts.status === 'completed' || ts.status === 'processed_for_invoice'
    );
    
    // Check if all convertible timesheets for this contractor are already selected
    const allSelected = convertibleTimesheets.every(ts => newSelection.has(ts.id));
    
    if (allSelected) {
      // Deselect all timesheets from this contractor
      convertibleTimesheets.forEach(ts => newSelection.delete(ts.id));
    } else {
      // Select all convertible timesheets from this contractor
      convertibleTimesheets.forEach(ts => newSelection.add(ts.id));
    }
    
    setSelectedTimesheets(newSelection);
  };
  
  const getWeekSelectionStatus = (weekTimesheets: WeeklyTimesheet[]) => {
    const convertibleTimesheets = weekTimesheets.filter(ts => 
      ts.status === 'completed' || ts.status === 'processed_for_invoice'
    );
    
    if (convertibleTimesheets.length === 0) return 'none';
    
    const selectedCount = convertibleTimesheets.filter(ts => selectedTimesheets.has(ts.id)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === convertibleTimesheets.length) return 'all';
    return 'partial';
  };
  
  const getContractorSelectionStatus = (contractorTimesheets: WeeklyTimesheet[]) => {
    const convertibleTimesheets = contractorTimesheets.filter(ts => 
      ts.status === 'completed' || ts.status === 'processed_for_invoice'
    );
    
    if (convertibleTimesheets.length === 0) return 'none';
    
    const selectedCount = convertibleTimesheets.filter(ts => selectedTimesheets.has(ts.id)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === convertibleTimesheets.length) return 'all';
    return 'partial';
  };

  const selectAllTimesheets = () => {
    const allConvertibleIds = filteredTimesheets
      .filter(ts => ts.status === 'completed' || ts.status === 'processed_for_invoice')
      .map(ts => ts.id);
    setSelectedTimesheets(new Set(allConvertibleIds));
  };

  const deselectAllTimesheets = () => {
    setSelectedTimesheets(new Set());
  };

  const handleBatchConvertToInvoices = () => {
    if (selectedTimesheets.size === 0) return;
    
    // Navigate to batch converter with selected IDs
    const selectedIds = Array.from(selectedTimesheets);
    navigate(`/timesheets/batch-convert?ids=${selectedIds.join(',')}`);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Timesheets</h1>
            <p className="text-gray-600 mt-1">Manage weekly timesheet entries for all jobs</p>
          </div>
          
          {/* Main Action Button */}
          <button
            onClick={() => navigate('/timesheets/entry')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition-all duration-200 transform hover:scale-105"
          >
            <Plus className="h-5 w-5 mr-2" />
            Start New Timesheet
          </button>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/timesheets/import')}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Excel
          </button>
          
          <button
            onClick={() => navigate('/timesheets/reports')}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FileText className="h-4 w-4 mr-2" />
            Generate Reports
          </button>
          
          <button
            onClick={() => navigate('/timesheets/employee-review')}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Users className="h-4 w-4 mr-2" />
            Employee Review
          </button>
          
          <button
            onClick={() => navigate('/timesheets/export-gusto')}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Gusto
          </button>
          
          <button
            onClick={() => setBatchMode(!batchMode)}
            className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
              batchMode 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Package className="h-4 w-4 mr-2" />
            {batchMode ? 'Exit Batch Mode' : 'Batch Convert'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Batch Conversion Controls */}
      {batchMode && (
        <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Package className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-blue-900">Batch Invoice Conversion</h3>
              </div>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md font-medium text-sm">
                {selectedTimesheets.size} selected
              </span>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={selectAllTimesheets}
                className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              >
                Select All Convertible
              </button>
              <button
                onClick={deselectAllTimesheets}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={handleBatchConvertToInvoices}
                disabled={selectedTimesheets.size === 0}
                className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Convert {selectedTimesheets.size} to Invoices
              </button>
            </div>
          </div>
          <p className="text-blue-700 text-sm mt-2">
            Select timesheets with 'completed' or 'processed for invoice' status to batch convert them to invoices.
          </p>
        </div>
      )}

      {/* Filters */
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search jobs or employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
              <option value="processed_for_invoice">Processed for Invoice</option>
            </select>
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setDateFilter('');
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>
      }

      {filteredTimesheets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {timesheets.length === 0 ? 'Get started by creating your first timesheet.' : 'Try adjusting your filters.'}
          </p>
          {timesheets.length === 0 && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/timesheets/entry')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Start New Timesheet
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {getGroupedTimesheets().map((group) => {
            const isExpanded = expandedWeeks.has(group.weekEnding);
            
            return (
              <div key={group.weekEnding} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Group Header - Clickable to expand/collapse */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="flex items-center cursor-pointer hover:bg-blue-100 rounded p-1 -m-1 transition-colors mr-2"
                        onClick={() => toggleWeekExpansion(group.weekEnding)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-blue-600" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                      <h2 className="text-lg font-semibold text-gray-900">
                        Week Ending: {group.displayDate}
                      </h2>
                      
                      {/* Week-level batch selection */}
                      {batchMode && (
                        <div className="ml-4 flex items-center">
                          <button
                            onClick={() => {
                              const allWeekTimesheets = group.contractors.flatMap(c => c.timesheets);
                              selectWeekTimesheets(allWeekTimesheets);
                            }}
                            className="flex items-center text-sm text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded transition-colors"
                          >
                            {(() => {
                              const allWeekTimesheets = group.contractors.flatMap(c => c.timesheets);
                              const status = getWeekSelectionStatus(allWeekTimesheets);
                              if (status === 'all') {
                                return <><CheckSquare className="h-4 w-4 mr-1" /> Deselect Week</>;
                              } else if (status === 'partial') {
                                return <><MinusSquare className="h-4 w-4 mr-1" /> Select Week</>;
                              } else {
                                return <><Square className="h-4 w-4 mr-1" /> Select Week</>;
                              }
                            })()} 
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 space-x-4">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md font-medium">
                        {group.totalTimesheets} timesheet{group.totalTimesheets !== 1 ? 's' : ''}
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md font-medium">
                        {group.totalHours.toFixed(1)} total hours
                      </span>
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md font-medium">
                        {group.contractors.length} contractor{group.contractors.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content - Contractors and their timesheets */}
                {isExpanded && (
                  <div className="p-6">
                    {group.contractors.map((contractor, contractorIndex) => (
                      <div key={contractor.contractorName} className={contractorIndex > 0 ? 'mt-8' : ''}>
                        {/* Contractor Header */}
                        <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
                          <Users className="h-5 w-5 text-purple-600 mr-2" />
                          <h3 className="text-lg font-medium text-gray-800">
                            {contractor.contractorName}
                          </h3>
                          <span className="ml-3 bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm font-medium">
                            {contractor.timesheets.length} timesheet{contractor.timesheets.length !== 1 ? 's' : ''}
                          </span>
                          <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm font-medium">
                            {contractor.timesheets.reduce((total, ts) => total + getTotalHours(ts), 0).toFixed(1)} hours
                          </span>
                          
                          {batchMode && (
                            <button
                              onClick={() => selectContractorTimesheets(contractor.timesheets)}
                              className="ml-4 flex items-center text-sm text-purple-700 hover:text-purple-900 bg-purple-100 hover:bg-purple-200 px-2 py-1 rounded transition-colors"
                            >
                              {(() => {
                                const status = getContractorSelectionStatus(contractor.timesheets);
                                if (status === 'all') {
                                  return <><CheckSquare className="h-4 w-4 mr-1" /> Deselect</>;
                                } else if (status === 'partial') {
                                  return <><MinusSquare className="h-4 w-4 mr-1" /> Select</>;
                                } else {
                                  return <><Square className="h-4 w-4 mr-1" /> Select</>;
                                }
                              })()} 
                            </button>
                          )}
                        </div>
                        
                        {/* Timesheets for this contractor */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                          {contractor.timesheets.map((timesheet) => {
                            const isSelectable = batchMode && (timesheet.status === 'completed' || timesheet.status === 'processed_for_invoice');
                            const isSelected = selectedTimesheets.has(timesheet.id);
                            
                            return (
                              <div key={timesheet.id} className={`bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200 ${
                                batchMode ? (isSelectable ? 'cursor-pointer' : 'opacity-60') : ''
                              } ${
                                isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                              }`}>
                                <div className="p-4">
                                  <div className="flex items-start justify-between">
                                    {batchMode && isSelectable && (
                                      <div className="flex items-center mr-3">
                                        <button
                                          onClick={() => toggleTimesheetSelection(timesheet.id)}
                                          className="text-blue-600 hover:text-blue-800"
                                        >
                                          {isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
                                        </button>
                                      </div>
                                    )}
                                    <div className="flex-1">
                                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                                        {timesheet.jobName}
                                      </h4>
                                      {timesheet.jobNumber && (
                                        <p className="text-sm text-gray-600 mb-2">
                                          Job #{timesheet.jobNumber}
                                        </p>
                                      )}
                                    </div>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(timesheet.status)}`}>
                                      {timesheet.status.replace('_', ' ')}
                                    </span>
                                  </div>

                                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-500">Employees:</span>
                                      <span className="ml-1 font-semibold text-gray-900">
                                        {getEmployeeCount(timesheet)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Hours:</span>
                                      <span className="ml-1 font-semibold text-blue-600">
                                        {getTotalHours(timesheet).toFixed(1)}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="mt-3 text-xs text-gray-500">
                                    Created {timesheet.createdAt.toLocaleDateString()}
                                  </div>

                                  <div className="mt-4 flex justify-between items-center pt-3 border-t border-gray-200">
                                    <div className="flex space-x-1">
                                      <button
                                        onClick={() => setSelectedTimesheet(timesheet)}
                                        className="inline-flex items-center px-2 py-1 text-xs bg-white text-gray-700 rounded-md hover:bg-gray-100 transition-colors border border-gray-300"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        View
                                      </button>
                                      <button
                                        onClick={() => navigate(`/timesheets/entry/${timesheet.id}`)}
                                        className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Edit
                                      </button>
                                      
                                      {(timesheet.status === 'completed' || timesheet.status === 'processed_for_invoice') && (
                                        <button
                                          onClick={() => navigate(`/timesheets/convert-to-invoice/${timesheet.id}`)}
                                          className={`inline-flex items-center px-2 py-1 text-xs rounded-md transition-colors ${
                                            timesheet.status === 'processed_for_invoice' 
                                              ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' 
                                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                                          }`}
                                        >
                                          <FileText className="h-3 w-3 mr-1" />
                                          {timesheet.status === 'processed_for_invoice' ? 'Re-Invoice' : 'Invoice'}
                                        </button>
                                      )}
                                    </div>

                                    <button
                                      onClick={() => handleDeleteTimesheet(timesheet)}
                                      className="inline-flex items-center px-2 py-1 text-xs bg-red-50 text-red-700 rounded-md hover:bg-red-100 transition-colors"
                                    >
                                      <Trash2 className="h-3 w-3 mr-1" />
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Timesheet Detail View Modal */}
      {selectedTimesheet && (
        <TimesheetDetailView
          timesheet={selectedTimesheet}
          onClose={() => setSelectedTimesheet(null)}
        />
      )}
    </div>
  );
};

export default TimesheetManagement;